"use client"

import { useEffect, type ReactNode } from "react"
import { usePathname, useRouter } from "next/navigation"
import { useAuth } from "@/hooks/use-auth"
import { useTenantsQuery } from "@/hooks/queries/tenants"
import { roleCanImpersonate } from "@/lib/auth/permissions.utils"
import { isSelectableTenant } from "@/lib/auth/selectable-tenant"
import { AppStartupLoader } from "@/components/app-startup-loader"
import { StaffOrganizationSelector } from "./staff-organization-selector"
import { ORGANIZATION_SELECTION_PATH, organizationReturnTo } from "@/lib/auth/organization-selection"

/** Presentation gate only; existing impersonation endpoints authorize selection. */
export function StaffOrganizationGate({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const isSelector = pathname === ORGANIZATION_SELECTION_PATH
  const { user, isLoading } = useAuth()
  const needsSelection = (pathname === "/" || isSelector) && Boolean(user?.role && roleCanImpersonate(user.role))
    && user?.context_revision === 0 && !user.impersonated_tenant_id
  const query = useTenantsQuery({ enabled: needsSelection })
  const tenants = (query.data ?? []).filter(isSelectableTenant)
  const showSelector = needsSelection && (query.isError || Boolean(query.data && tenants.length > 1))

  useEffect(() => {
    if (isLoading) return
    if (showSelector && !isSelector) {
      const destination = organizationReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`)
      router.replace(`${ORGANIZATION_SELECTION_PATH}?returnTo=${encodeURIComponent(destination)}`)
    } else if (isSelector && (!needsSelection || (query.data && !query.isError && tenants.length <= 1))) {
      router.replace(organizationReturnTo(new URLSearchParams(window.location.search).get("returnTo")))
    }
  }, [isLoading, isSelector, showSelector, needsSelection, query.data, query.isError, tenants.length, router])

  if ((pathname === "/" || isSelector) && isLoading) return <AppStartupLoader />
  if (isSelector && !needsSelection) return <AppStartupLoader />
  if (!needsSelection || !user) return children
  if (!query.data && !query.isError) return <AppStartupLoader />
  if (showSelector && !isSelector) return <AppStartupLoader />
  if (showSelector) {
    return <StaffOrganizationSelector tenants={tenants} homeTenantId={user.tenant_id}
      loadFailed={query.isError} retrying={query.isFetching} onRetry={() => void query.refetch()} />
  }
  if (isSelector) return <AppStartupLoader />
  return children
}
